import React, { useState, useEffect } from 'react';
import { ContraparteInput, ContraparteValor, camposDeContraparte } from '../common/ContraparteInput';
import { InventarioService } from '../../services/administration/inventarioService';
import { Stock } from '../../types/administracion';
import { getCurrentUserInfo, getRepositories } from '../../services/storage';
import ConfirmationModal from '../common/ConfirmationModal';
import { getLocalISODate } from '../../utils/dateHelper';
import { formatMoney } from '../../utils/formatNumber';

interface VentaInventarioFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface VentaFormData {
  fecha: string;
  stock_id: string;
  cantidad: number | null;
  precio_venta: number | null;
  descripcion: string;
}

/**
 * Form component for registering stock sales
 */
export const VentaInventarioForm: React.FC<VentaInventarioFormProps> = ({
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState<VentaFormData>({
    fecha: getLocalISODate(),
    stock_id: '',
    cantidad: null,
    precio_venta: null,
    descripcion: ''
  });

  // A quién se le vendió (2026-10-18). Opcional.
  const [contraparte, setContraparte] = useState<ContraparteValor | null>(null);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [selectedStock, setSelectedStock] = useState<Stock | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStocks, setLoadingStocks] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [empresaId, setEmpresaId] = useState<string>('');
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Load available stocks on mount
  useEffect(() => {
    loadStocks();
  }, []);

  // Update selected stock when stock_id changes
  useEffect(() => {
    if (formData.stock_id) {
      const stock = stocks.find(s => s.id === formData.stock_id);
      setSelectedStock(stock || null);
      // Suggest current price as default sale price
      if (stock) {
        // Precio de venta se calcula on-demand, no usamos precio_unitario almacenado
        setFormData(prev => ({ ...prev, precio_venta: 0 }));
      }
    } else {
      setSelectedStock(null);
    }
  }, [formData.stock_id, stocks]);

  // Load empresa_id from profiles, fallback to first available empresa
  useEffect(() => {
    const loadEmpresaId = async () => {
      try {
        const currentUser = await getCurrentUserInfo();
        let empresaIdToUse = currentUser?.empresaId;

        // If no empresa in profile, try to get first available empresa
        if (!empresaIdToUse) {
          const { empresa } = getRepositories();
          const empresas = await empresa.getAll();
          if (empresas.length > 0) {
            empresaIdToUse = empresas[0].id;
            console.log('Using first available empresa:', empresas[0].nombre, empresaIdToUse);
          }
        }

        if (empresaIdToUse) {
          setEmpresaId(empresaIdToUse);
        } else {
          console.error('No active empresa found in profiles or empresas table');
          setError('No se encontró una empresa activa. Creá una empresa primero.');
        }
      } catch (err) {
        console.error('Error loading empresa_id:', err);
        setError('Error al cargar la información de la empresa.');
      }
    };

    loadEmpresaId();
  }, []);

  const loadStocks = async () => {
    try {
      setLoadingStocks(true);
      const availableStocks = await InventarioService.obtenerStocksDisponibles();
      setStocks(availableStocks);
    } catch (err: any) {
      console.error('Error cargando stocks:', err);
      setError('Error al cargar el inventario disponible');
    } finally {
      setLoadingStocks(false);
    }
  };

  // Use reusable validation from InventarioService
  const validateVenta = async (stockId: string, cantidad: number, fecha: string): Promise<{ isValid: boolean; errorMessage?: string }> => {
    try {
      // Use the reusable FIFO validation function
      const validationResult = await InventarioService.validateStockAvailability(stockId, cantidad, fecha);
      return validationResult;
    } catch (error) {
      console.error('Error validating venta:', error);
      return {
        isValid: false,
        errorMessage: 'Error al validar la disponibilidad de inventario.'
      };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.stock_id) {
        setErrorMessage('Debe seleccionar un producto de inventario');
        setErrorModalOpen(true);
        return;
      }
      if (formData.cantidad === null || formData.cantidad <= 0) {
        setErrorMessage('La cantidad debe ser mayor a 0');
        setErrorModalOpen(true);
        return;
      }
      if (formData.precio_venta === null || formData.precio_venta <= 0) {
        setErrorMessage('El precio de venta debe ser mayor a 0');
        setErrorModalOpen(true);
        return;
      }

      // Validate stock availability on the selected date using reusable FIFO validation
      const validationResult = await validateVenta(formData.stock_id, formData.cantidad, formData.fecha);
      if (!validationResult.isValid) {
        setErrorMessage(validationResult.errorMessage || `No hay suficiente inventario disponible para esta cantidad en la fecha seleccionada.`);
        setErrorModalOpen(true);
        return;
      }

      // Validate available quantity
      if (selectedStock && formData.cantidad !== null && formData.cantidad > selectedStock.cantidad) {
        setErrorMessage(`Inventario insuficiente. Disponible: ${selectedStock.cantidad} ${selectedStock.unidad_medida}`);
        setErrorModalOpen(true);
        return;
      }

      // Register sale
      await InventarioService.registrarVenta({
        fecha_consumo: formData.fecha, // Using fecha_consumo as generic event date in DTO
        stock_id: formData.stock_id,
        cantidad: formData.cantidad,
        precio_venta: formData.precio_venta,
        descripcion: formData.descripcion.trim() || undefined,
        empresaId: empresaId,
        userId: undefined, // TODO: Get from auth context
        ...camposDeContraparte(contraparte),
      });
      setContraparte(null);

      // Reset form
      setFormData({
        fecha: getLocalISODate(),
        stock_id: '',
        cantidad: 0,
        precio_venta: 0,
        descripcion: ''
      });

      // Reload stocks to update available quantities
      await loadStocks();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error registrando venta:', err);
      setErrorMessage(err.message || 'Error al registrar la venta');
      setErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div>
      <h3 className="text-xl font-semibold text-ink mb-6">
        Registrar Venta de Inventario
      </h3>

      {error && (
        <div className="border border-danger/30 bg-danger/10 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <span className="text-danger mr-2">⚠️</span>
            <span className="text-danger">{error}</span>
          </div>
        </div>
      )}

      {loadingStocks ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest mx-auto mb-4"></div>
          <p className="text-sage">Cargando inventario disponible...</p>
        </div>
      ) : stocks.length === 0 ? (
        <div className="border border-warning/30 bg-warning/10 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <span className="text-warning mr-2">⚠️</span>
            <div>
              <p className="text-warning font-medium">No hay inventario disponible</p>
              <p className="text-warning text-sm">
                No hay productos en inventario para vender.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-ink mb-2">
                Fecha de Venta <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                max={getLocalISODate()}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink mb-2">
                Producto a Vender <span className="text-danger">*</span>
              </label>
              <select
                className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
                value={formData.stock_id}
                onChange={(e) => setFormData({ ...formData, stock_id: e.target.value, cantidad: 0 })}
                required
              >
                <option value="">Elegí un producto...</option>
                {stocks.map((stock) => (
                  <option key={stock.id} value={stock.id}>
                    {stock.nombre} ({stock.categoria}) - Disp: {stock.cantidad} {stock.unidad_medida}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedStock && (
            <div className="bg-paper-2/40 border border-hairline rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-ink mb-3">Detalles del Producto</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-ink">Categoría:</span>
                  <span className="ml-2 text-ink">{selectedStock.categoria}</span>
                </div>
                <div>
                  <span className="font-medium text-ink">Disponible:</span>
                  <span className="ml-2 text-ink">{selectedStock.cantidad} {selectedStock.unidad_medida}</span>
                </div>
                <div>
                  <span className="font-medium text-ink">Costo Unitario:</span>
                  <span className="ml-2 text-ink">Calculado on-demand</span>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-ink mb-2">
                Cantidad <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
                value={formData.cantidad ?? ''}
                onChange={(e) => setFormData({ ...formData, cantidad: e.target.value === '' ? null : parseFloat(e.target.value) })}
                min="0.01"
                step="0.01"
                max={selectedStock?.cantidad || undefined}
                required
                disabled={!formData.stock_id}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink mb-2">
                Precio de Venta Unitario <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
                value={formData.precio_venta ?? ''}
                onChange={(e) => setFormData({ ...formData, precio_venta: e.target.value === '' ? null : parseFloat(e.target.value) })}
                min="0.01"
                step="0.01"
                required
                disabled={!formData.stock_id}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink mb-2">
                Total Venta
              </label>
              <div className="w-full rounded-md border border-hairline bg-paper-2/40 px-3 py-2 text-ink font-medium">
                {formatMoney((formData.cantidad || 0) * (formData.precio_venta || 0))}
              </div>
            </div>
          </div>


          <div className="mb-6">
            <ContraparteInput id="venta-stock-contraparte" label="De quién (comprador)" value={contraparte} onChange={setContraparte} />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-ink mb-2">
              Notas (Opcional)
            </label>
            <textarea
              className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink placeholder:text-sage/70 resize-y focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
              rows={3}
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Detalles de la venta, cliente, etc..."
            />
          </div>

          <div className="border border-forest/30 bg-forest/10 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <span className="text-forest mr-2 mt-0.5">💰</span>
              <div>
                <p className="text-forest font-medium mb-1">Impacto Financiero</p>
                <p className="text-forest text-sm">
                  Esta operación registrará un ingreso de dinero y descontará el stock del inventario.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-hairline">
            {onCancel && (
              <button
                type="button"
                className="px-6 py-2.5 border border-hairline rounded-md text-ink bg-paper hover:bg-hairline/50 transition-colors"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              className="px-6 py-2.5 bg-forest text-paper rounded-md hover:bg-forest-mid disabled:opacity-50 transition-colors flex items-center space-x-2"
              disabled={loading || loadingStocks || stocks.length === 0}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-paper"></div>
                  <span>Registrando...</span>
                </>
              ) : (
                <>
                  <span>✓</span>
                  <span>Registrar Venta</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}
      {/* Error Modal - same as LedgerModal */}
      <ConfirmationModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        onConfirm={() => setErrorModalOpen(false)}
        title="Error de Validación"
        message={errorMessage}
        confirmText="Entendido"
        cancelText=""
        isDestructive={false}
      />
    </div>
  );
};
